import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BackLayerContext } from '../ui/backLayer/backLayerContext';

const MARKER = 'mobileTransient';

/**
 * Verwalter des Zurück-Fängers: ein inhaltsloser Chronik-Eintrag, der die Zurück-Geste
 * abfängt, solange eine flüchtige Fläche (Sheet, Dialog, Modus, Inline-Eingabe) offen ist.
 *
 * Invariante: offene Flächen > 0 ⟺ GENAU EIN Fänger in der Chronik. Nur diese Datei fasst
 * die Chronik dafür an. Rechnete jede Fläche selbst, entstünden beim gleichzeitigen
 * Schließen mehrerer Flächen zwei Zurück-Schritte — der zweite wird vom Browser häufig
 * verworfen, und im Zweifel wird der Editor verlassen.
 *
 * Die Übergänge laufen über eine Phase statt über einen Vorher-Vergleich: React ruft
 * Effekte im StrictMode doppelt auf, und ein Vorher-Ref läse den zweiten Durchlauf als
 * neue Lage — die App navigierte beim Laden zurück.
 *
 * Der Fänger trägt keinen Inhalt: flüchtige Flächen werden nach einem Reload NICHT
 * wiederhergestellt — anders als die Vollbild-Ebenen (`useHistoryLayer`), die ihren
 * Zustand aus dem Chronik-Eintrag rekonstruieren.
 *
 * Bekannte Grenzen: Zwei sehr schnelle Zurück-Drücke können den Editor verlassen — der neue
 * Fänger für die darunterliegende Fläche entsteht erst nach dem Pop. Und der Verwalter trägt
 * die oberste Fläche ab, ohne zu wissen, ob sie wirklich schließt: Tut ihr `onClose` nichts,
 * bleibt sie sichtbar, ist aber nicht mehr im Stapel und verliert damit still ihren
 * Zurück-Schutz. Das ist die Kehrseite davon, dass hier niemand auf eine Abmeldung wartet —
 * die Alternative wäre ein Fänger, der bei jedem Durchlauf neu gelegt wird.
 *
 * Stapeltiefe liegt als `document.body.dataset.backLayerDepth` offen: Prüfanker für Unit-
 * und E2E-Tests, damit sie internen Zustand nicht nachbauen müssen. Beim Unmount räumt der
 * Provider das Merkmal ab, sonst erfüllt ein Vorgänger-Test die Erwartung des nächsten.
 */
export function BackLayerProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const stackRef = useRef([]);          // [{ id, onClose }] — oberste Fläche zuletzt
  const phaseRef = useRef('none');      // 'none' | 'pushing' | 'present' | 'closing'
  const [depth, setDepth] = useState(0);
  const hasMarker = Boolean(location.state?.[MARKER]);
  // Die Kaskade liest Location und `navigate` über Refs, damit der Kontextwert über die
  // gesamte Laufzeit dieselbe Identität behält. Hinge er an der Location, meldeten sich bei
  // JEDER Navigation alle offenen Flächen ab und neu an: der Stapel bekäme die Baum- statt
  // der Öffnungs-Reihenfolge, und eine gerade abgetragene Fläche stünde sofort wieder drin.
  const locationRef = useRef(location);
  locationRef.current = location;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const gelegtAufRef = useRef(null);    // Location, auf der der Fänger gelegt wurde

  const register = useCallback((id, onClose) => {
    stackRef.current = [...stackRef.current.filter((s) => s.id !== id), { id, onClose }];
    setDepth(stackRef.current.length);
  }, []);

  const unregister = useCallback((id) => {
    stackRef.current = stackRef.current.filter((s) => s.id !== id);
    setDepth(stackRef.current.length);
  }, []);

  useEffect(() => {
    document.body.dataset.backLayerDepth = String(depth);
  }, [depth]);

  useEffect(() => () => { delete document.body.dataset.backLayerDepth; }, []);

  useEffect(() => {
    // Die Aufräum-Regel darf in dem Durchlauf nicht greifen, in dem ein angeforderter Sprung
    // noch unterwegs ist: dort gilt kurz „nichts offen und ein Fänger liegt da", und ein
    // Eingriff nähme den Eintrag weg, den der Sprung gerade abbaut.
    const darfAufraeumen = phaseRef.current !== 'closing';

    // Nach JEDEM Phasenwechsel wird die Lage erneut bewertet, statt den nächsten Lauf des
    // Effekts abzuwarten. Ein Wechsel löst die Lage manchmal nur halb auf — der Sprung kommt an,
    // während sich im selben Render eine neue Fläche angemeldet hat. Danach steht alles still:
    // Location, Stapeltiefe und Merkmal ändern sich nicht mehr, der Effekt liefe nie wieder, und
    // zurück bliebe eine offene Fläche ohne Fänger. Der nächste Zurück-Druck verließe den Editor.
    // Wechsel IN die Warte-Phasen `pushing`/`closing` beenden den Durchlauf: dort ist eine
    // Navigation unterwegs, deren Ankunft den Effekt ohnehin weckt.
    // Die längste mögliche Kette ist drei Bewertungen (Warte-Phase → `present` → abtragen →
    // `none`); die Schranke ist das Netz gegen einen künftig eingebauten Zyklus.
    for (let runde = 0; runde < 4; runde += 1) {
      const phase = phaseRef.current;

      if (phase === 'pushing') {
        // Der Rückfall greift erst, wenn die Location sich bewegt hat. Steht noch dieselbe, ist
        // der Fänger schlicht unterwegs — ein zweiter Push legte einen zweiten Eintrag, und der
        // erste Zurück-Druck bliebe wirkungslos. Genau das passiert im StrictMode, der jeden
        // Effekt beim Mounten ein zweites Mal mit unveränderter Location aufruft, und ebenso,
        // wenn zwei Flächen unmittelbar nacheinander aufgehen.
        if (!hasMarker && gelegtAufRef.current === location) return;
        // Kommt die erwartete Lage nicht (eine Navigation, die nie ankommt, weil zwischenzeitlich
        // die Route wechselt), fällt die Maschine auf `none` zurück und bewertet neu — die
        // Übergänge sind idempotent. Ohne diesen Rückfall bliebe sie stecken und die
        // Zurück-Mechanik wäre für die Sitzung tot.
        phaseRef.current = hasMarker ? 'present' : 'none';
        continue;
      }

      if (phase === 'closing') {
        // Der Sprung ist durch. Ob er auf einem Eintrag mit oder ohne Merkmal gelandet ist,
        // entscheidet die folgende Bewertung; die Aufräum-Regel bleibt dabei gesperrt.
        phaseRef.current = 'none';
        continue;
      }

      if (phase === 'present') {
        if (!hasMarker) {
          // Zurück gedrückt. Die oberste Fläche wird SYNCHRON aus dem Stapel genommen, nicht
          // erst über ihre Abmeldung im nächsten Commit: `depth` ist hier der alte Wert, und
          // ein Weiterrechnen darauf legte einen Fänger für eine Fläche, die gleich weg ist
          // (Push und sofortiger Pop bei jedem Zurück-Druck, Vorwärts-Zweig zerstört). Bleibt
          // eine Fläche fälschlich offen, weil ihr `onClose` nichts tut, wäre es sogar eine
          // Endlosschleife. Die spätere `unregister`-Abmeldung ist dann ein Filter ohne Wirkung.
          phaseRef.current = 'none';
          const oberste = stackRef.current[stackRef.current.length - 1];
          stackRef.current = stackRef.current.slice(0, -1);
          setDepth(stackRef.current.length);
          oberste?.onClose();
          // Liegt noch eine Fläche darunter, braucht sie sofort einen neuen Fänger — das
          // erledigt die folgende `none`-Bewertung. War es die letzte, findet sie nichts zu tun.
          continue;
        }
        if (depth === 0) {
          phaseRef.current = 'closing';
          navigate(-1);
          return;
        }
        // Fänger liegt, Flächen sind offen — die Invariante stimmt.
        return;
      }

      // phase === 'none' — gerechnet wird auf dem Stapel selbst, nicht auf `depth`: nach dem
      // synchronen Abtragen oben ist `depth` noch der Wert aus dem Render.
      const offen = stackRef.current.length;
      if (offen > 0 && !hasMarker) {
        phaseRef.current = 'pushing';
        gelegtAufRef.current = location;
        navigate(location.pathname + location.search + location.hash, {
          state: { ...location.state, [MARKER]: true },
        });
        return;
      }
      if (offen === 0 && hasMarker && darfAufraeumen) {
        // Vorgefunden (Reload, Vorwärts-Taste, Rückkehr aus anderer Route): ersetzen statt
        // navigieren, sonst läuft man dem Nutzer davon und die Vorwärts-Taste wirkt kaputt.
        // Über die Router-API, nie `history.replaceState`: das zerstört die key/idx-Buchführung,
        // an der das gezählte Zurückgehen hängt.
        const { [MARKER]: _weg, ...rest } = location.state || {};
        navigate(location.pathname + location.search + location.hash, { replace: true, state: rest });
      }
      return;
    }
  }, [hasMarker, depth, navigate, location]);

  /**
   * Schließt eine Vollbild-Ebene samt allem, was über ihr liegt — Ebenen UND Fänger — in
   * EINEM gezählten Schritt. Zwei einzelne Zurück-Schritte im selben Durchlauf wendet der
   * Browser asynchron an und verwirft dabei häufig den zweiten.
   */
  const closeLayersDownTo = useCallback((layerId) => {
    const aktuell = locationRef.current;
    const layers = aktuell.state?.mobileLayers || [];
    const index = layers.findIndex((l) => l.id === layerId);
    if (index < 0) return;
    const layerSteps = layers.length - index;      // die Ebene selbst plus alles darüber
    // Aus dem Location-State, nicht aus der Phase: das ist die exakte Auskunft darüber,
    // ob über der Ebene ein Fänger liegt — auch mitten in einem Phasenwechsel.
    const markerSteps = aktuell.state?.[MARKER] ? 1 : 0;
    const flaechen = [...stackRef.current].reverse();
    stackRef.current = [];
    setDepth(0);
    phaseRef.current = 'closing';
    flaechen.forEach((s) => s.onClose());
    navigateRef.current(-(layerSteps + markerSteps));
  }, []);

  // Netz für Aufrufer, die den Verwalter umgehen: `useHistoryLayer.close()` ruft blind
  // navigate(-1) und würde bei offener Fläche den Fänger erwischen statt die Ebene.
  const hasOpenSurfaces = useCallback(() => stackRef.current.length > 0, []);

  const wert = useMemo(
    () => ({ register, unregister, closeLayersDownTo, hasOpenSurfaces }),
    [register, unregister, closeLayersDownTo, hasOpenSurfaces]
  );

  return <BackLayerContext.Provider value={wert}>{children}</BackLayerContext.Provider>;
}
